import { FinancingApi } from '@servicetitan/financing-api-client';
import { useDependencies } from '@servicetitan/react-ioc';
import { useQuery } from '@tanstack/react-query';

interface UseLegalDisclosureOptions {
    providerName: string | undefined;
    tenantId: string | undefined;
    category: string | string[] | undefined;
}

export const useLegalDisclosure = (options: UseLegalDisclosureOptions) => {
    const { providerName, tenantId, category } = options;
    const [apiClient] = useDependencies(FinancingApi);
    const { data, isSuccess } = useQuery({
        queryKey: ['legalDisclosuresGetLegalDisclosure', providerName, tenantId],
        queryFn: () =>
            apiClient.legalDisclosuresGetLegalDisclosures({
                providerName: providerName!,
                tenantId: tenantId!,
            }),
        enabled: tenantId !== undefined && providerName !== undefined && category != null,
    });

    if (isSuccess && data == null) {
        return Array.isArray(category) ? [] : '';
    }

    if (data?.data?.disclosures == null || data?.data?.disclosures?.length === 0) {
        return Array.isArray(category) ? [] : '';
    }

    if (Array.isArray(category)) {
        // If category is an array, find all matching disclosures and return array of texts
        const disclosures = data?.data?.disclosures;
        if (!disclosures) {
            return [];
        }

        return category
            .map(cat => {
                const entry = disclosures.find(
                    ({ category: c }) => c?.toLowerCase() === cat.toLowerCase(),
                );
                return entry?.text ?? '';
            })
            .filter(text => text !== ''); // Filter out empty strings
    }

    // Handle single category
    const disclosureEntry = data?.data.disclosures.find(
        ({ category: c }) => c?.toLowerCase() === category?.toLowerCase(),
    );

    return disclosureEntry?.text ?? '';
};
